"use client";

import { subscribeNewsletterAction } from "@/app/actions";
import { getCaptchaToken } from "@/services/google";
import { loadScript } from "@/utils/scripts";
import classNames from "classnames";
import { useState } from "react";
import InputField from "./form/input-field";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const [error, setError] = useState("");

  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY;

  const onSubmit = async () => {
    const trimmedEmail = email.trim();

    const hasAt = trimmedEmail.includes("@");
    const hasDotAfterAt = hasAt && trimmedEmail.split("@")[1]?.includes(".");

    if (!trimmedEmail) {
      setError("Fyll i din e-postadress i formatet email@email.com");
      return;
    }

    if (!hasAt) {
      setError(
        "E-postadressen saknar @. Fyll i din e-postadress i formatet email@email.com"
      );
      return;
    }

    if (!hasDotAfterAt) {
      setError(
        "E-postadressen saknar en punkt, fyll i din e-postadress i formatet: email@email.com"
      );
      return;
    }

    const simpleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!simpleEmailRegex.test(trimmedEmail)) {
      setError(
        "Ogiltig e-postadress. Fyll i din e-postadress i formatet email@email.com"
      );
      return;
    }

    let token = "";
    if (siteKey) {
      await loadScript({
        src: `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
        async: true,
        defer: true,
        waitFor: () =>
          new Promise<void>((resolve, reject) => {
            const test = (tries: number) => {
              if (typeof (window as any)?.grecaptcha?.execute !== "undefined") {
                resolve();
              } else if (tries > 40) {
                reject();
              } else {
                setTimeout(() => test(tries + 1), tries < 10 ? 100 : 350);
              }
            };

            test(0);
          }),
      });
      
      token = await getCaptchaToken("subscribe_newsletter", siteKey);
    }
    else {
      token = "captcha-disabled";
    }

    const response = await subscribeNewsletterAction(trimmedEmail, token);

    if (response?.success) {
      setHasSubscribed(true);
    } else {
      setError("Ett fel inträffade försök igen");
    }
  };

  return (
    <div className="relative max-w-[748px]">
      <div
        aria-hidden={!hasSubscribed}
        className={classNames(
          "flex space-x-4 md:space-x-8 transition-opacity absolute inset-0",
          hasSubscribed ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="171"
          height="171"
          fill="none"
          viewBox="0 0 171 171"
        >
          <path
            fill="#831310"
            fillRule="evenodd"
            d="M125.589 19.645C118.142 17.208 108 17.1 85.499 17.1c-12.068 0-20.69.005-27.486.469-6.72.458-11.025 1.336-14.523 2.785A42.75 42.75 0 0020.354 43.49c-1.449 3.499-2.327 7.804-2.786 14.523-.463 6.796-.468 15.419-.468 27.487 0 12.069.005 20.692.468 27.488.459 6.718 1.337 11.024 2.786 14.522a42.75 42.75 0 0023.136 23.136c3.498 1.449 7.804 2.327 14.523 2.786 6.795.464 15.418.468 27.487.468 12.068 0 20.691-.004 27.487-.468 6.718-.459 11.024-1.337 14.522-2.786a42.75 42.75 0 0023.136-23.136c1.449-3.498 2.327-7.804 2.786-14.522.464-6.796.468-15.42.468-27.488 0-6.547 0-12.108-.078-16.962a8.55 8.55 0 0117.098-.275c.08 5.006.08 10.699.08 17.182v.37c0 11.685 0 20.89-.508 28.336-.518 7.59-1.592 13.973-4.047 19.903a59.855 59.855 0 01-32.391 32.391c-5.929 2.455-12.313 3.529-19.903 4.047-7.446.508-16.65.508-28.335.508h-.63c-11.685 0-20.89 0-28.336-.508-7.59-.518-13.974-1.592-19.903-4.047a59.853 59.853 0 01-32.39-32.391c-2.456-5.93-3.53-12.313-4.048-19.903C0 106.705 0 97.501 0 85.815v-.63C0 73.5 0 64.296.508 56.85c.518-7.59 1.592-13.973 4.048-19.902a59.85 59.85 0 0132.39-32.39C42.876 2.1 49.26 1.026 56.85.507 64.295 0 73.499 0 85.185 0h2.153c20.089-.003 33.186-.005 43.571 3.394a8.55 8.55 0 01-5.32 16.251zm44.245 5.971a8.55 8.55 0 01-3.077 11.694l-1.895 1.105a178.995 178.995 0 00-70.93 76.643 8.548 8.548 0 01-13.74 2.325L45.879 83.105a8.55 8.55 0 1112.085-12.098l26.081 26.054a196.095 196.095 0 0172.201-73.416l1.896-1.106a8.55 8.55 0 0111.693 3.077z"
            clipRule="evenodd"
          ></path>
        </svg>

        <div>
          <h2 className="font-extrabold text-2xl md:text-4xl mb-4 md:mb-8">
            Tack för din anmälan!
          </h2>

          <p className="max-w-[586px]">
            Du har anmält dig till EFN:s nyhetsbrev! Du kommer nu att få
            nyhetsbrev med de senaste nyheterna och analyserna från EFN
            Ekonomikanalen och EFN Finansmagasinet till din e-post.
          </p>
        </div>
      </div>

      <div
        aria-hidden={hasSubscribed}
        className={classNames(
          "transition-opacity",
          hasSubscribed ? "opacity-0 pointer-events-none" : "opacity-100"
        )}
      >
        <h2 className="font-extrabold text-2xl md:text-4xl mb-4 md:mb-8">
          Vill du ha tillgång till de senaste ekonominyheterna från EFN? -
          Prenumerera på vårt nyhetsbrev!
        </h2>

        <form
          className="flex mb-2"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className="relative w-full">
            <InputField
              label="E-postadress"
              name="newsletter-email"
              id="newsletter-email"
              placeholder="Ange e-postadress"
              type="email"
              required
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <button
              type="submit"
              className="btn btn-primary h-12 px-4 absolute right-2 top-2"
            >
              Prenumerera
            </button>
          </div>
        </form>

        <p className="max-w-[586px] ml-4">
          Genom att prenumerera godkänner du att din e-postadress sparas för att
          vi ska kunna skicka nyhetsbrev till dig
        </p>

        {Boolean(error) && (
          <div className="text-red-500 border-2 border-red-500 p-3 mt-4">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterForm;
